import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../atoms/Card';
import { Button } from '../atoms/Button';
import { useAuth } from '../../hooks/useAuth';
import { getFirebaseAuth } from '../../services/firebaseConfig';
import { isEmailVerified, resendEmailVerification } from '../../services/auth/authService';
import { isMfaEnrolled } from '../../services/auth/mfaService';
import './auth-pages.css';

const USE_FIREBASE = (process.env.REACT_APP_DATA_PROVIDER || 'firebase') === 'firebase';

export const VerifyEmailPending: React.FC = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (isEmailVerified()) {
      navigate(isMfaEnrolled() ? '/home' : '/mfa-setup', { replace: true });
    }
  }, [authLoading, navigate, user]);

  const handleCheckVerification = async () => {
    setActionLoading(true);
    setError('');
    setMessage('');
    try {
      if (USE_FIREBASE) {
        const fbUser = getFirebaseAuth().currentUser;
        if (fbUser) {
          await fbUser.reload();
        }
      }
      if (isEmailVerified()) {
        navigate('/mfa-setup', { replace: true });
        return;
      }
      setError('Your email is not verified yet. Check spam or promotions, then try again.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check verification status.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResend = async () => {
    setActionLoading(true);
    setError('');
    setMessage('');
    try {
      await resendEmailVerification();
      setMessage('Verification email sent. Check your inbox, spam, and promotions folders.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend verification email.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUseDifferentEmail = async () => {
    setActionLoading(true);
    try {
      await logout();
      navigate('/create-account', { replace: true });
    } finally {
      setActionLoading(false);
    }
  };

  const handleBackToSignIn = async () => {
    setActionLoading(true);
    try {
      await logout();
      navigate('/login', { replace: true });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Card className="auth-card" padding="lg" shadow="lg">
        <div className="create-account__header">
          <img src="/Transparent-Logo.png" alt="Macha Group" className="create-account__logo" />
          <h2 className="create-account__title">Verify Your Email</h2>
          <p className="create-account__subtitle">
            Your account was created, but email verification is still required before phone verification.
          </p>
        </div>

        {error && <div className="create-account__error">{error}</div>}
        {message && <p className="ok-note">{message}</p>}

        <div className="create-account__form" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className="forgot-password__success">
            <div className="forgot-password__success-icon">EV</div>
            <h3>Check Your Email</h3>
            <p>
              We sent a verification link to <strong>{user?.email || 'your email address'}</strong>.
            </p>
            <p className="forgot-password__success-hint">
              If you do not see it, check spam, junk, or promotions first.
            </p>
          </div>

          <Button type="button" fullWidth loading={actionLoading} size="lg" onClick={handleCheckVerification}>
            I&apos;ve Verified My Email
          </Button>
          <Button type="button" fullWidth size="lg" variant="secondary" onClick={handleResend} disabled={actionLoading}>
            Resend Verification Email
          </Button>
          <Button type="button" fullWidth size="lg" variant="ghost" onClick={handleUseDifferentEmail} disabled={actionLoading}>
            Use a Different Email
          </Button>

          <Button type="button" fullWidth size="lg" variant="ghost" onClick={handleBackToSignIn} disabled={actionLoading}>
            Back to Sign In
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default VerifyEmailPending;
